import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { HiPlus } from 'react-icons/hi2';
import { FaFileExcel } from 'react-icons/fa';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import CheckboxMultiSelect from '../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import { MPSwitch } from '../../../../../../components/ui';
import StockSearchBar from '../StockSearchBar/StockSearchBar';
import { getProductStockByLocalId, setReportedStock, updateMinimumStock, toggleStockFavorite, toggleStockFollowing } from '../../../../../../crud/management_api/stock_management';
import { ProductStock, StockShortage, Merma } from '../../types';
import { LocalSession } from '../../../../../../types/LocalSession';
import { exportStockToExcel } from '../../utils/stockExcelExport';

// Import child components
import CreateStock from './components/CreateStock/CreateStock';
import DeleteStock from './components/DeleteStock/DeleteStock';
import EqualStock from './components/Displayer/components/EqualStock/EqualStock';
import StockDetails from './components/StockDetails/StockDetails';
import TransferStock from './components/Displayer/components/TransferStock/TransferStock';
import Displayer from './components/Displayer/Displayer';

// Type for editing state now includes the field being edited
export type EditingInfoType = { id: number; storageId: number; field: 'reported' | 'min' } | null;

interface GeneralProps {
  lastSession?: LocalSession | null;
}

const General: React.FC<GeneralProps> = ({ 
  lastSession = null
}) => {
    // --- STATE MANAGEMENT ---
    const [productStockData, setProductStockData] = useState<ProductStock[]>([]);
    const [originalProducts, setOriginalProducts] = useState<ProductStock[]>([]);
    const [filteredStockList, setFilteredStockList] = useState<ProductStock[]>([]);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

    // Editing State
    const [editingInfo, setEditingInfo] = useState<EditingInfoType>(null);
    const [tempReportedStock, setTempReportedStock] = useState<string>('');
    const [tempMinStock, setTempMinStock] = useState<string>(''); // New state for min stock editing

    // Modal and selection state
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedStorageId, setSelectedStorageId] = useState<number>(0);
    const [selectedStockItem, setSelectedStockItem] = useState<ProductStock | null>(null);
    const [selectedReportedStock, setSelectedReportedStock] = useState<number>(0);
    const [selectedStorageName, setSelectedStorageName] = useState<string>('');
    const [showMovementsModal, setShowMovementsModal] = useState<boolean>(false);
    const [showCreateStockModal, setShowCreateStockModal] = useState<boolean>(false);
    const [showTransferStockModal, setShowTransferStockModal] = useState<boolean>(false);
    const [showDeleteStockModal, setShowDeleteStockModal] = useState<boolean>(false);
    const [showEqualizeModal, setShowEqualizeModal] = useState(false);
    const [currentEqualizeInfo, setCurrentEqualizeInfo] = useState<{ id: number; product_id: string; storageId: number; system_stock: number; } | null>(null);

    // Filter state
    const [selectedAcronyms, setSelectedAcronyms] = useState<any[]>([]);
    const [selectedStorages, setSelectedStorages] = useState<any[]>([]);
    const [selectedStockStatus, setSelectedStockStatus] = useState<Array<{ value: string; label: string }>>([]);
    const [currentSessionOnly, setCurrentSessionOnly] = useState<boolean>(false);

    // Derived filter options
    const storageOptions = useMemo(() => {
      const uniqueStorages = [...new Set(originalProducts.map(item => item.storage_unit_name).filter(Boolean))];
      return uniqueStorages.map(storage => ({ value: storage, label: storage }));
    }, [originalProducts]);

    const stockStatusOptions = useMemo(() => [
      { value: 'bajo', label: 'Bajo Stock' },
      { value: 'optimo', label: 'Óptimo' },
      { value: 'excedente', label: 'Excedente' }
    ], []);

    // --- DATA FETCHING & FILTERING ---
    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
          setIsLoadingData(true);
          try {
            const user = JSON.parse(localStorage.getItem('user') ?? '{}');
            const localId = user ? Number(user.local_id) : 0;
            if (!localId) { console.warn('Local ID not found'); return; }
            const response = await getProductStockByLocalId(localId);
            if (isMounted) {
              setProductStockData(response.data);
              setOriginalProducts(response.data);
            }
          } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar el stock.');
          } finally {
            if (isMounted) setIsLoadingData(false);
          }
        };
        fetchData();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        const filtered = originalProducts.filter((product) => {
            const matchAcronym = selectedAcronyms.length === 0 || selectedAcronyms.some((option) => option.value === product.acronym);
            const matchStorage = selectedStorages.length === 0 || selectedStorages.some((option) => option.value === product.storage_unit_name);
            
            // Stock status filter
            let matchStockStatus = true;
            if (selectedStockStatus.length > 0) {
                const status = getStockStatus(product);
                matchStockStatus = selectedStockStatus.some(option => option.value === status);
            }
            
            // Current session filter
            let matchCurrentSession = true;
            if (currentSessionOnly && lastSession && lastSession.session_start) {
                if (product.last_stock_update) {
                    const updateDate = new Date(product.last_stock_update);
                    const sessionStart = new Date(lastSession.session_start);
                    matchCurrentSession = updateDate >= sessionStart;
                } else {
                    matchCurrentSession = false;
                }
            }
            
            return matchAcronym && matchStorage && matchStockStatus && matchCurrentSession;
        });
        setProductStockData(filtered);
    }, [originalProducts, selectedAcronyms, selectedStorages, selectedStockStatus, currentSessionOnly, lastSession]);
    
    // Helper function to get stock status
    const getStockStatus = (stock: ProductStock): string => {
        if (stock.min_quantity === null || stock.min_quantity === undefined) return 'sin-minimo';
        if (stock.reported_stock < stock.min_quantity) return 'bajo';
        if (stock.reported_stock <= stock.min_quantity * 1.05) return 'optimo';
        return 'excedente';
    };

    // --- HANDLERS ---
    const handleUpdateReportedStock = async (id: number, product_id: string, storageId: number, newValue: number) => {
        const current = originalProducts.find(item => item.product_id === product_id && item.storage_unit_id === storageId);
        if (!current) { toast.error('Producto no encontrado.'); return; }
    
        const prevProducts = originalProducts; // snapshot for rollback
        // Optimistic UI: update immediately
        const optimisticUpdated: ProductStock = { ...current, system_stock: newValue, reported_stock: newValue, last_stock_update: new Date().toISOString() };
        const optimisticList = originalProducts.map((item) => (item.product_id === product_id && item.storage_unit_id === storageId) ? optimisticUpdated : item);
        setOriginalProducts(optimisticList);
    
        const payload = { reported_stock: newValue, ingredient_id: id, storage_unit_id: storageId };
        try {
          await setReportedStock(payload);
          toast.success('Stock actualizado correctamente.');
        } catch (error) {
          console.error('Error al actualizar el stock:', error);
          // Rollback on failure
          setOriginalProducts(prevProducts);
          toast.error('Error al actualizar el stock.');
        }
    };

    /**
     * Updates the minimum stock for an item on the frontend.
     * @param {ProductStock} item - The full stock item object.
     * @param {number} newMinValue - The new minimum quantity.
     */
    const handleUpdateMinStock = (item: ProductStock, newMinValue: number) => {
        const prevProducts = originalProducts; // snapshot for rollback
        // Optimistic UI: update state immediately for instant feedback
        const optimisticList = originalProducts.map(p =>
            p.product_id === item.product_id && p.storage_unit_id === item.storage_unit_id
                ? { ...p, min_quantity: newMinValue }
                : p
        );
        setOriginalProducts(optimisticList);
        updateMinimumStock(item.id, item.storage_unit_id, newMinValue).then(() => {
            toast.success('Stock mínimo actualizado correctamente.');
        }).catch((error) => {
            console.error('Error al actualizar el stock mínimo:', error);   
            // Rollback on failure
            setOriginalProducts(prevProducts);
            toast.error('Error al actualizar el stock mínimo.');
        });    
    };

    // Optimistic toggle handlers for favorite and following
    const handleToggleFavorite = async (item: ProductStock) => {
        const prevProducts = [...originalProducts]; // snapshot for rollback
        const currentIsFavorite = (item as any).is_favorite || false;
        
        // Optimistic UI: update state immediately for instant feedback
        const optimisticList = originalProducts.map(p =>
            p.product_id === item.product_id && p.storage_unit_id === item.storage_unit_id
                ? { ...p, is_favorite: !currentIsFavorite }
                : p
        );
        setOriginalProducts(optimisticList);
        
        try {
            const ingredientId = parseInt(item.product_id.split('-')[1]);
            await toggleStockFavorite(ingredientId, item.storage_unit_id);
            // Success - optimistic update was correct, no additional action needed
        } catch (error) {
            console.error('Error toggling favorite:', error);
            // Rollback on failure
            setOriginalProducts(prevProducts);
            toast.error('Error al actualizar favoritos');
        }
    };

    const handleToggleFollowing = async (item: ProductStock) => {
        const prevProducts = [...originalProducts]; // snapshot for rollback
        const currentIsFollowing = (item as any).is_following || false;
        
        // Optimistic UI: update state immediately for instant feedback
        const optimisticList = originalProducts.map(p =>
            p.product_id === item.product_id && p.storage_unit_id === item.storage_unit_id
                ? { ...p, is_following: !currentIsFollowing }
                : p
        );
        setOriginalProducts(optimisticList);
        
        try {
            const ingredientId = parseInt(item.product_id.split('-')[1]);
            await toggleStockFollowing(ingredientId, item.storage_unit_id);
            // Success - optimistic update was correct, no additional action needed
        } catch (error) {
            console.error('Error toggling following:', error);
            // Rollback on failure
            setOriginalProducts(prevProducts);
            toast.error('Error al actualizar monitoreo');
        }
    };

    // Excel Export Handler
    const handleExportToExcel = () => {
        if (!filteredStockList || filteredStockList.length === 0) {
            toast.warning('No hay datos de stock para exportar');
            return;
        }

        try {
            // For now, we'll export only the stock data
            // Empty arrays for shortages and mermas since they're in different views
            const emptyShortages: StockShortage[] = [];
            const emptyMermas: Merma[] = [];
            
            exportStockToExcel(filteredStockList, emptyShortages, emptyMermas);
            toast.success('Excel exportado exitosamente');
        } catch (error) {
            console.error('Error exporting stock to Excel:', error);
            toast.error('Error al exportar a Excel. Por favor, intenta nuevamente.');
        }
    };

    return (
        <div className="flex flex-col gap-3 w-full">
            {/* Unified toolbar: search + filters + actions in one row */}
            <div className="mp-toolbar flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-[1_1_220px] min-w-[150px] max-w-[300px] z-[100]">
                <StockSearchBar
                  data={productStockData}
                  updateFilteredData={setFilteredStockList}
                  placeholder="Buscar stock..."
                />
              </div>

              {/* Filter dropdowns */}
              <div className="min-w-[100px] flex-shrink-0">
                <CheckboxMultiSelect
                  options={storageOptions}
                  value={selectedStorages}
                  onChange={setSelectedStorages}
                  placeholder="Almacén"
                />
              </div>
              <div className="min-w-[100px] flex-shrink-0">
                <CheckboxMultiSelect
                  options={stockStatusOptions}
                  value={selectedStockStatus}
                  onChange={(selected: any) => setSelectedStockStatus(selected as Array<{ value: string; label: string }>)}
                  placeholder="Estado"
                />
              </div>

              {/* Session toggle */}
              <MPSwitch
                label="Sesión Actual"
                checked={currentSessionOnly}
                onChange={() => setCurrentSessionOnly(!currentSessionOnly)}
              />

              {/* Actions — right-aligned */}
              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                <button
                  className="mp-btn-export"
                  onClick={handleExportToExcel}
                  disabled={isLoadingData || filteredStockList.length === 0}
                  title="Exportar stock a Excel"
                >
                  <FaFileExcel />
                  <span>Excel</span>
                </button>
                <button
                  className="mp-btn-primary"
                  onClick={() => setShowCreateStockModal(true)}
                >
                  <HiPlus className="w-4 h-4" />
                  Crear Stock
                </button>
              </div>
            </div>

            {isLoadingData ? (
                <LoadingSpinner text="Cargando stock..." transparent={true} />
            ) : (
                <Displayer
                    productStockData={originalProducts}
                    filteredStockList={filteredStockList}
                    editingInfo={editingInfo}
                    tempReportedStock={tempReportedStock}
                    tempMinStock={tempMinStock}
                    selectedAcronyms={selectedAcronyms}
                    setSelectedAcronyms={setSelectedAcronyms}
                    setShowCreateStockModal={setShowCreateStockModal}
                    selectedStorages={selectedStorages}
                    setSelectedStorages={setSelectedStorages}
                    setEditingInfo={setEditingInfo}
                    setTempReportedStock={setTempReportedStock}
                    setTempMinStock={setTempMinStock}
                    onUpdateReportedStock={handleUpdateReportedStock}
                    onUpdateMinStock={handleUpdateMinStock}
                    onViewMovements={(item) => { setSelectedStockItem(item); setShowMovementsModal(true); }}
                    onEqualize={(item) => { setCurrentEqualizeInfo({ id: item.id, product_id: item.product_id, storageId: item.storage_unit_id, system_stock: item.system_stock }); setShowEqualizeModal(true); }}
                    onTransfer={(item) => { setSelectedItemId(item.product_id); setSelectedStorageId(item.storage_unit_id); setSelectedReportedStock(item.reported_stock); setSelectedStorageName(item.storage_unit_name); setShowTransferStockModal(true); }}
                    onDelete={(item) => { setSelectedItemId(item.product_id); setSelectedStorageId(item.storage_unit_id); setShowDeleteStockModal(true); }}
                    onToggleFavorite={handleToggleFavorite}
                    onToggleFollowing={handleToggleFollowing}
                />
            )}

            {/* --- Modals --- */}
            <StockDetails show={showMovementsModal} onClose={() => setShowMovementsModal(false)} stockItem={selectedStockItem} />
            <CreateStock isOpen={showCreateStockModal} onClose={() => setShowCreateStockModal(false)} setProductStockList={setOriginalProducts} originalProducts={originalProducts} setOriginalProducts={setOriginalProducts} />
            <TransferStock isOpen={showTransferStockModal} onClose={() => setShowTransferStockModal(false)} storageid={selectedStorageId} stockItemId={selectedItemId} reportedStock={selectedReportedStock} storageUnitName={selectedStorageName} setProductStockData={setOriginalProducts} />
            <DeleteStock product_id={selectedItemId} storage_unit_id={selectedStorageId} show={showDeleteStockModal} handleClose={() => setShowDeleteStockModal(false)} setProductStockData={setOriginalProducts} productStockData={originalProducts} />
            <EqualStock show={showEqualizeModal} setShow={setShowEqualizeModal} productStockData={originalProducts} setProductStockData={setOriginalProducts} setOriginalProducts={setOriginalProducts} currentEqualizeInfo={currentEqualizeInfo} setCurrentEqualizeInfo={setCurrentEqualizeInfo} />
        </div>
    );
};

export default General;